import React from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import {
  Banner,
  Button,
  Dialog,
  H4,
  HStack,
  Heading,
  Icon,
  Separator,
  Tag,
  Text,
  VStack,
} from '@betterment/samba-react';
import { Wrap } from '@betterment/samba-styled-system/jsx';
import { ErrorBoundary, SambaAutoLink } from '@betterment/shared-components';

import { IdempotencyProviderWithFeatureRegistry } from '../../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { CreateAccountRecommendationDocument } from '../graphql/mutation.createAccountRecommendation.generated';

const GRADIENT_SHIFT_KEYFRAMES = `
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const Content: React.FC = () => {
  const [createAccountRecommendation, { data, loading }] = useGraphQLMutation(CreateAccountRecommendationDocument);

  const recommendation = data?.createAccountRecommendation?.result;

  return (
    <VStack css={{ gap: 'space.200' }}>
      <VStack css={{ gap: 'space.150' }}>
        <Dialog.Title asChild={true}>
          <Heading variant="title300" as="h2">
            Next best accounts for you
          </Heading>
        </Dialog.Title>
        <Dialog.Description asChild={true}>
          <Text variant="text100" color="text.weak">
            We'll recommend the next best accounts for you based on your financial situation with help from our
            expert-trained AI.
          </Text>
        </Dialog.Description>
      </VStack>
      <Banner>
        <Banner.Content>
          <HStack css={{ gap: 'space.100', alignItems: 'flex-start' }}>
            <Text variant="text100">
              BettermentAI is trained by our team of CFP® advisors. Your personal data isn't shared.
            </Text>
          </HStack>
        </Banner.Content>
      </Banner>
      <Separator />
      <VStack css={{ gap: 'space.50' }}>
        <HStack css={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <H4 variant="title200" color="text.weak">
            Is this info still correct?
          </H4>
          <SambaAutoLink to={urlBuilder.appFinancialProfileEdit.absolute()}>Edit</SambaAutoLink>
        </HStack>
        <Text variant="text50" color="text.weak">
          We use this to provide your recommendation.
        </Text>
      </VStack>
      <Wrap>
        <Tag accent="grey">Annual income: $Fake</Tag>
        <Tag accent="grey">Investable assets: $Fake</Tag>
        <Tag accent="grey">Tax bracket: Fake%</Tag>
        <Tag accent="grey">Filing status: Fake</Tag>
        <Tag accent="grey">Spousal annual income: $Fake</Tag>
      </Wrap>
      <HStack css={{ gap: 'space.100', marginTop: 'space.200' }}>
        <Button onClick={() => createAccountRecommendation()} disabled={loading} variant="solidPrimary">
          {loading ? 'Loading...' : 'See my recommendation'}
        </Button>
        <Dialog.Close asChild={true}>
          <Button variant="ghostPrimary">I'm not interested</Button>
        </Dialog.Close>
      </HStack>
      {recommendation && <Text variant="text100">{recommendation}</Text>}
    </VStack>
  );
};

export const AccountRecommendationModal: React.FC = () => {
  return (
    <Dialog>
      <Dialog.Trigger asChild={true}>
        <Button
          variant="textSecondary"
          css={{
            padding: '1px',
            borderRadius: '20px',
            background:
              'linear-gradient(90deg, token(colors.icon.accent.gold), token(colors.icon.accent.blue), token(colors.icon.accent.gold))',
            backgroundSize: '200% 200%',
            animation: 'gradientShift 3s ease infinite',
            textDecoration: 'none',
          }}
        >
          <style>{GRADIENT_SHIFT_KEYFRAMES}</style>
          <HStack
            css={{
              px: 'space.100',
              py: 'space.50',
              borderRadius: '20px',
              background: 'background.surface',
              gap: 'space.50',
              alignItems: 'center',
            }}
          >
            <Icon name="Guidance" size="xsmall" color="icon.accent.gold" />
            <Text variant="text50" color="text" css={{ lineHeight: '1' }}>
              Get a recommendation
            </Text>
          </HStack>
        </Button>
      </Dialog.Trigger>
      <Dialog.Content size="large" css={{ p: 'space.400', width: '60%', maxWidth: '800px' }}>
        <ErrorBoundary fallbackType="full" feature="account recommendation" owner="agents">
          <IdempotencyProviderWithFeatureRegistry featureName="createAccountRecommendation">
            <Content />
          </IdempotencyProviderWithFeatureRegistry>
        </ErrorBoundary>
      </Dialog.Content>
    </Dialog>
  );
};
